import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  IconButton,
  Alert,
  Link,
  Tooltip
} from '@mui/material';
import {
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../../../services/api';
import * as XLSX from 'xlsx';
import { generateSignedUrlForFilename } from '../../../utils/s3Utils';

const SEZExemption = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    labType: '',
    userName: '',
    transactionStatus: '',
    dateFrom: null,
    dateTo: null
  });

  // Modal states
  const [acceptModal, setAcceptModal] = useState({ open: false, appId: null });
  const [rejectModal, setRejectModal] = useState({ open: false, appId: null });
  const [rollbackModal, setRollbackModal] = useState({ open: false, appId: null });
  
  // Modal form states
  const [rejectRemarks, setRejectRemarks] = useState('');

  const labTypes = [
    { value: '1', label: 'Testing' },
    { value: '2', label: 'Calibration' },
    { value: '3', label: 'Medical' },
    { value: '4', label: 'PTP' },
    { value: '5', label: 'RMP' }
  ];

  const transactionStatuses = [
    { value: '0', label: 'Pending' },
    { value: '1', label: 'Accepted' },
    { value: '2', label: 'Rejected' }
  ];

  // Fetch data function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        labType: filters.labType || null,
        userName: filters.userName || null,
        status: filters.transactionStatus || null,
        dateFrom: filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : null,
        dateTo: filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : null
      };

      // Remove null values
      Object.keys(params).forEach(key => params[key] === null && delete params[key]);

      const response = await api.get('/accounts/sez-exemption', { params });
      
      setData(response.data.data || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      console.error('Error fetching SEZ exemption data:', err);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setPage(0); // Reset to first page when filtering
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      labType: '',
      userName: '',
      transactionStatus: '',
      dateFrom: null,
      dateTo: null
    });
    setPage(0);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Action handlers
  const handleAccept = async () => {
    try {
      await api.post('/accounts/sez-exemption/accept', {
        appId: acceptModal.appId,
        orgId: localStorage.getItem('orgId') // Assuming orgId is stored in localStorage after login
      });
      
      setAcceptModal({ open: false, appId: null });
      fetchData();
      alert('Accepted successfully');
    } catch (err) {
      alert('Error occurred while accepting');
    }
  };

  const handleReject = async () => {
    try {
      await api.post('/accounts/sez-exemption/reject', {
        appId: rejectModal.appId,
        orgId: localStorage.getItem('orgId'), // Assuming orgId is stored in localStorage after login
        remarks: rejectRemarks
      });
      
      setRejectModal({ open: false, appId: null });
      setRejectRemarks('');
      fetchData();
      alert('Rejected successfully');
    } catch (err) {
      alert('Error occurred while rejecting');
    }
  };

  const handleRollback = async () => {
    try {
      await api.post('/accounts/sez-exemption/rollback', {
        appId: rollbackModal.appId,
        orgId: localStorage.getItem('orgId') // Assuming orgId is stored in localStorage after login
      });
      
      setRollbackModal({ open: false, appId: null });
      fetchData();
      alert('Rollback has been done successfully');
    } catch (err) {
      alert('Error occurred while rollback');
    }
  };

  // Handle file view/download with S3 signed URL
  const handleViewFile = async (fileName) => {
    if (!fileName) {
      alert('No file available to view');
      return;
    }

    try {
      setLoading(true);
      // Generate signed URL for the file
      const signedUrl = await generateSignedUrlForFilename(fileName);
      
      if (signedUrl) {
        // Open the signed URL in a new tab
        window.open(signedUrl, '_blank');
      } else {
        alert('Unable to generate file access URL');
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      alert('Error accessing file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Excel download
  const handleExcelDownload = async () => {
    try {
      const params = {
        labType: filters.labType || null,
        userName: filters.userName || null,
        status: filters.transactionStatus || null,
        dateFrom: filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : null,
        dateTo: filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : null,
        exportAll: true
      };

      // Remove null values
      Object.keys(params).forEach(key => params[key] === null && delete params[key]);

      const response = await api.get('/accounts/sez-exemption', { params });
      const exportData = response.data.data || [];

      if (exportData.length === 0) {
        alert('No data to download');
        return;
      }

      // Prepare data for Excel export
      const excelData = exportData.map((item, index) => ({
        'S. No': index + 1,
        'Lab ID': item.applicationNo,
        'Laboratory Name': item.laboratoryName,
        'Username': item.loginname,
        'Lab Type': item.labType,
        'Email': item.contactEmail,
        'Mobile': item.contactMobile,
        'Address': item.address,
        'State': item.state,
        'City': item.city,
        'Pincode': item.pin,
        'Status': item.status,
        'Date': new Date(item.date).toLocaleDateString('en-IN')
      }));

      // Create and download Excel file
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SEZ Exemption Report');
      XLSX.writeFile(wb, `SEZ_Exemption_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error downloading Excel:', err);
      alert('Error downloading file');
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'warning';
      case 'Accepted': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          SEZ Exemption Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Lab Type</InputLabel>
                <Select
                  value={filters.labType}
                  label="Lab Type"
                  onChange={(e) => handleFilterChange('labType', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {labTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Username"
                value={filters.userName}
                onChange={(e) => handleFilterChange('userName', e.target.value)}
                placeholder="Enter username"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Date From"
                value={filters.dateFrom}
                onChange={(value) => handleFilterChange('dateFrom', value)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={new Date()}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Date To"
                value={filters.dateTo}
                onChange={(value) => handleFilterChange('dateTo', value)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={new Date()}
              />
            </Grid>
          </Grid>

          {/* Transaction Status Radio Group */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Status Filter:</Typography>
            <RadioGroup
              row
              value={filters.transactionStatus}
              onChange={(e) => handleFilterChange('transactionStatus', e.target.value)}
            >
              <FormControlLabel value="" control={<Radio />} label="All" />
              <FormControlLabel value="0" control={<Radio />} label="Pending" />
              <FormControlLabel value="1" control={<Radio />} label="Accepted" />
              <FormControlLabel value="2" control={<Radio />} label="Rejected" />
            </RadioGroup>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleResetFilters}
            >
              Reset Filters
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExcelDownload}
              disabled={loading}
            >
              Download Excel
            </Button>
          </Box>
        </Paper>

        {/* Data Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Actions</TableCell>
                  <TableCell>S.No</TableCell>
                  <TableCell>Lab ID</TableCell>
                  <TableCell>Laboratory Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Lab Type</TableCell>
                  <TableCell>SEZ File</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Pincode</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      <Typography variant="h6">No Data Found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((item, index) => (
                    <TableRow key={item.appId}>
                      <TableCell>
                        {item.status === 'Pending' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => setAcceptModal({ open: true, appId: item.appId })}
                            >
                              Accept
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => setRejectModal({ open: true, appId: item.appId })}
                            >
                              Reject
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Chip 
                              label={item.status} 
                              color={getStatusColor(item.status)}
                              size="small"
                            />
                            {/* <Button
                              variant="contained"
                              color="info"
                              size="small"
                              onClick={() => setRollbackModal({ open: true, appId: item.appId })}
                            >
                              Rollback
                            </Button> */}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{item.applicationNo}</TableCell>
                      <TableCell>{item.laboratoryName}</TableCell>
                      <TableCell>{item.loginname}</TableCell>
                      <TableCell>{item.labType}</TableCell>
                      <TableCell>
                        {item.sezFile ? (
                          <Tooltip title="View/Download SEZ File">
                            <IconButton
                              color="primary"
                              onClick={() => handleViewFile(item.sezFile)}
                              disabled={loading}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                '&:hover': { backgroundColor: 'primary.50' }
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          'No file'
                        )}
                      </TableCell>
                      <TableCell>{item.contactEmail}</TableCell>
                      <TableCell>{item.contactMobile}</TableCell>
                      <TableCell>{item.address}</TableCell>
                      <TableCell>{item.StateName}</TableCell>
                      <TableCell>{item.cityName}</TableCell>
                      <TableCell>{item.pin}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Accept Modal */}
        <Dialog open={acceptModal.open} onClose={() => setAcceptModal({ open: false, appId: null })}>
          <DialogTitle>Confirm Accept SEZ Exemption</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ pt: 1 }}>
              Are you sure you want to accept this SEZ exemption application?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAcceptModal({ open: false, appId: null })}>Cancel</Button>
            <Button onClick={handleAccept} variant="contained" color="primary">
              Accept
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={rejectModal.open} onClose={() => setRejectModal({ open: false, appId: null })}>
          <DialogTitle>Reject SEZ Exemption</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Please provide remarks for rejection:
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Rejection Remarks *"
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              placeholder="Enter reason for rejection..."
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectModal({ open: false, appId: null })}>Cancel</Button>
            <Button 
              onClick={handleReject} 
              variant="contained" 
              color="error"
              disabled={!rejectRemarks.trim()}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rollback Modal */}
        <Dialog open={rollbackModal.open} onClose={() => setRollbackModal({ open: false, appId: null })}>
          <DialogTitle>Confirm Rollback Exemption</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ pt: 1 }}>
              Are you sure you want to rollback this exemption? This action will reset the exemption status to pending.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRollbackModal({ open: false, appId: null })}>Cancel</Button>
            <Button onClick={handleRollback} variant="contained" color="warning">
              Rollback
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default SEZExemption;